import styled from '@emotion/styled';
import {
    HeadingText30,
    Modal,
    Button,
    BodyTextSmall,
    CalloutInline,
} from '@modernhealth/vital-components';
import { isNil } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
    CoachingVisitNode,
    CoachingVisitState,
    CostEstimation,
    ProductType,
} from 'src/generated/webapp_gql';
import { ProviderType } from 'src/modules/user/models/user';
import { getVideoSessions } from 'src/modules/videoSessions/selectors/videoSessionsSelectors';
import { useGetVisitRatesQuery } from '../documents/getVisitRates.generated';
import { CostModalTable } from './CostModalTable';

const Text = styled(BodyTextSmall)({
    marginBottom: 20,
});

const StyledButton = styled(Button)({
    width: '100%',
    marginTop: 10,
});

const StyledHeading = styled(HeadingText30)({
    marginBottom: 20,
});

const SectionWrapper = styled.div({
    marginBottom: 20,
});

const CalloutContainer = styled.div({
    marginBottom: '20px',
});

interface Props {
    visible: boolean;
    onConfirm: () => void;
    onClose: () => void;
    costEstimation?: CostEstimation;
    type?: string | null;
    isPamm?: boolean;
}

export const CostModal: React.FC<Props> = ({
    visible,
    onClose,
    onConfirm,
    costEstimation,
    type,
    isPamm,
}) => {
    const { t } = useTranslation();

    const isHDHP = type === 'HDHP';

    let perSessionCostTable;
    let explanation;
    let calculationText;

    if (isHDHP) {
        perSessionCostTable = [
            !isNil(costEstimation?.remainingDeductible) && [
                t('payments:cost_modal.remaining_deductible'),
                `$${costEstimation?.remainingDeductible}`,
            ],
            !isNil(costEstimation?.coinsurance) && [
                t('payments:cost_modal.percentage'),
                `${(costEstimation?.coinsurance as number) * 100}%`,
            ],
        ];
        explanation = t('payments:cost_modal.explanation.hdhp');
        calculationText = t('payments:cost_modal.cost_calculation.hdhp');
    } else {
        perSessionCostTable = [
            !isNil(costEstimation?.copay) && [
                t('payments:cost_modal.copay'),
                `$${costEstimation?.copay}`,
            ],
            !isNil(costEstimation?.outOfPocketRemaining) && [
                t('payments:cost_modal.remaining_oop'),
                `$${costEstimation?.outOfPocketRemaining}`,
            ],
        ];
        explanation = t('payments:cost_modal.explanation.ppo');
        calculationText = isPamm
            ? t('videoSessions:session_review.modal.description.nonhdhp.how')
            : t('payments:cost_modal.cost_calculation.ppo');
    }

    const hdhpOutOfPocketTable = [
        t('payments:cost_modal.remaining_oop'),
        `$${costEstimation?.outOfPocketRemaining}`,
    ];

    const { data, loading } = useGetVisitRatesQuery();
    const rates = data?.getVisitRates;
    const initialEvaluationCost = rates?.find(
        rate => rate?.productType === ProductType.PsychiatryInitialEvaluation,
    )?.rate;
    const followUpCost = rates?.find(
        rate => rate?.productType === ProductType.PsychiatryFollowUp,
    )?.rate;

    const psychiatryCostTable = [
        [
            t('videoSessions:session_review.initial_evaluation.lowercase'),
            initialEvaluationCost ? `$${initialEvaluationCost}` : '',
        ],
        [
            t('videoSessions:session_review.follow_up.lowercase'),
            followUpCost ? `$${followUpCost}` : '',
        ],
    ];

    const sessions = useSelector(getVideoSessions);

    const hasUpcomingSessions = sessions.some(
        (s: CoachingVisitNode) =>
            s.state === CoachingVisitState.Scheduled &&
            s.coachAssignment?.coach?.providerType === ProviderType.PSYCHIATRIST,
    );

    return (
        <Modal isVisible={visible} onCloseButtonClick={onClose} maxWidth={500}>
            <SectionWrapper>
                <StyledHeading> {t('payments:cost_modal.title')} </StyledHeading>
                <Text>
                    {isPamm
                        ? t('videoSessions:session_review.modal.description.shared')
                        : t('payments:cost_modal.explanation')}
                </Text>
                <Text>{explanation}</Text>
                {isPamm && isHDHP && !loading && (
                    <>
                        <Text>
                            {t('videoSessions:session_review.modal.description.hdhp.why.part2')}
                        </Text>
                        <CostModalTable data={psychiatryCostTable} />
                    </>
                )}
            </SectionWrapper>
            <SectionWrapper>
                <StyledHeading> {t('payments:cost_modal.cost_calculation')} </StyledHeading>
                {hasUpcomingSessions && isPamm && isHDHP && (
                    <CalloutContainer>
                        <CalloutInline variant="info">
                            {t('payments:multiple_session_warning')}
                        </CalloutInline>
                    </CalloutContainer>
                )}
                <Text>{calculationText}</Text>
                <CostModalTable data={perSessionCostTable} />
                {isHDHP && <Text>{t('payments:cost_modal.oop')}</Text>}
                {isHDHP && <CostModalTable data={[hdhpOutOfPocketTable]} />}
            </SectionWrapper>
            {!isPamm && (
                <StyledButton size="medium" onClick={onConfirm}>
                    {t('payments:proceed_pay')}
                </StyledButton>
            )}
        </Modal>
    );
};
